import React from 'react';
import { Lock } from 'lucide-react';
import { describeProjectLockBadge, ProjectLock } from '../services/projectLockLogic';

/**
 * Der Sperrhinweis an einer Projektzeile oder -kachel.
 *
 * Eine WARNUNG, kein Riegel: der Klick auf das Projekt funktioniert weiterhin,
 * es oeffnet dann eben im Nur-Ansicht-Modus mit dem gelben Balken. Der Hinweis
 * erspart lediglich die Ueberraschung.
 *
 * Die Komponente entscheidet NICHTS — Text, Anlass und die Unterscheidung
 * eigene/fremde Sperre kommen fertig aus `describeProjectLockBadge`
 * (projectLockLogic.ts), wo sie ohne jsdom pruefbar sind. Hier steht nur, wie
 * das aussieht.
 */
interface Props {
    lock: ProjectLock | null | undefined;
    currentUserId: string | null | undefined;
    /** Zeitpunkt fuer die Ablaufrechnung — kommt aus `useProjectLocks`. */
    nowMs: number;
    /**
     * Nur das Schloss, Text im `title`. Fuer enge Stellen (Kalenderzelle,
     * Kanban-Karte), an denen ein ganzer Satz die Zeile sprengen wuerde.
     */
    compact?: boolean;
    className?: string;
}

export const ProjectLockHint: React.FC<Props> = ({ lock, currentUserId, nowMs, compact = false, className = '' }) => {
    const badge = describeProjectLockBadge(lock, currentUserId, nowMs);
    if (!badge) return null;

    // Fremde Sperre bernsteinfarben wie der Balken ueber dem offenen Projekt —
    // derselbe Sachverhalt soll nicht zweimal verschieden aussehen. Die eigene
    // bleibt grau: sie haelt niemanden auf und darf nicht wie eine Warnung
    // wirken.
    const tone = badge.kind === 'other'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-slate-100 text-slate-500 border-slate-200';

    return (
        <span
            data-tutorial="sperre.hinweis"
            title={badge.title}
            aria-label={badge.title}
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 shrink-0 max-w-full ${tone} ${className}`}
        >
            <Lock className="w-2.5 h-2.5 shrink-0" />
            {!compact && <span className="truncate">{badge.label}</span>}
        </span>
    );
};
